import { useState, useEffect } from 'react';
import { UserCheck, UserX, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../config/api';
import InteractiveMap from '../components/map/InteractiveMap';

interface User {
   id: string;
   email: string;
   firstName: string;
   lastName: string;
   status: 'active' | 'inactive';
   accountType?: string;
   location: {
      id?: string;
      latitude: number;
      longitude: number;
      city: string;
      country: string;
      address?: string;
      placeId?: string;
      createdAt?: any;
      updatedAt?: any;
   } | null; // Can be null for users without location
   registrationDate: string;
   lastLogin?: string | null;
   lastActivity?: string | null;
   profile?: {
      photoURL?: string;
      coverPhotoURL?: string;
   } | null;
}

interface MapData {
   metrics: {
      active?: number;
      inactive?: number;
      // Fallback for old structure
      confirmedActive?: number;
      invited?: number;
      loggedInNotPaid?: number;
   };
   users: User[]; // All users
}

export const MapViewPage = () => {
   const [mapData, setMapData] = useState<MapData | null>(null);
   const [loading, setLoading] = useState(true);
   const [currentPage, setCurrentPage] = useState(1);
   const usersPerPage = 10;

   useEffect(() => {
      const fetchMapData = async () => {
         try {
            setLoading(true);
            // Try the main endpoint with real Firestore location data first
            const response = await api.get('/map');
            if (response.data.success) {
               setMapData(response.data.data);
               console.log('✅ Using real Firestore location data:', {
                  source: response.data.source,
                  userCount: response.data.data.users.length,
                  locationMappingCount: response.data.data.locationMapping?.length || 0,
                  metrics: response.data.data.metrics,
                  summary: response.data.summary,
               });
            }
         } catch (error) {
            console.error('Failed to fetch real location data, trying enhanced endpoint:', error);
            // Fallback to enhanced endpoint
            try {
               const enhancedResponse = await api.get('/map/enhanced');
               if (enhancedResponse.data.success) {
                  setMapData(enhancedResponse.data.data);
                  console.log('📊 Using enhanced map data fallback');
               }
            } catch (enhancedError) {
               console.error('Failed to fetch enhanced data, trying analytics-geo:', enhancedError);
               // Final fallback to Google Analytics geographic endpoint
               try {
                  const analyticsResponse = await api.get('/map/analytics-geo');
                  if (analyticsResponse.data.success) {
                     setMapData(analyticsResponse.data.data);
                     console.log('🌍 Using Google Analytics final fallback data');
                  }
               } catch (analyticsError) {
                  console.error('All map endpoints failed:', analyticsError);
               }
            }
         } finally {
            setLoading(false);
         }
      };

      fetchMapData();
   }, []);

   if (loading) {
      return (
         <div className='min-h-screen flex items-center justify-center'>
            <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600'></div>
         </div>
      );
   }

   if (!mapData) {
      return (
         <div className='min-h-screen flex items-center justify-center'>
            <div className='text-center'>
               <h2 className='text-2xl font-bold text-gray-900 mb-2'>Failed to load map data</h2>
               <p className='text-gray-600'>Please try refreshing the page.</p>
            </div>
         </div>
      );
   }

   // Sort users to bring active users to the top
   const sortedUsers =
      mapData?.users.sort((a, b) => {
         if (a.status === 'active' && b.status !== 'active') return -1;
         if (a.status !== 'active' && b.status === 'active') return 1;
         return 0;
      }) || [];

   // Calculate pagination
   const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
   const startIndex = (currentPage - 1) * usersPerPage;
   const endIndex = startIndex + usersPerPage;
   const currentUsers = sortedUsers.slice(startIndex, endIndex);

   return (
      <div className='space-y-4'>
         {/* Header */}
         <div className='border-b border-gray-200 pb-2'>
            <h1 className='text-xl font-bold leading-tight tracking-tight text-gray-900'>
               User Distribution Map
            </h1>
            <p className='mt-1 text-xs text-gray-600'>
               Geographic distribution of users across different engagement levels
            </p>
         </div>

         {/* Metrics Cards */}
         <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
            {/* Active Users */}
            <div className='bg-white overflow-hidden shadow rounded-lg'>
               <div className='p-3'>
                  <div className='flex items-center'>
                     <div className='flex-shrink-0'>
                        <UserCheck className='h-5 w-5 text-green-600' />
                     </div>
                     <div className='ml-3 w-0 flex-1'>
                        <dl>
                           <dt className='text-xs font-medium text-gray-500 truncate'>
                              Active Users
                           </dt>
                           <dd className='text-base font-medium text-gray-900'>
                              {(
                                 mapData.metrics.active ??
                                 mapData.metrics.confirmedActive ??
                                 0
                              ).toLocaleString()}
                           </dd>
                        </dl>
                     </div>
                  </div>
               </div>
               <div className='bg-green-50 px-3 py-2'>
                  <div className='text-xs'>
                     <span className='font-medium text-green-800'>Active in last 7 days</span>
                  </div>
               </div>
            </div>

            {/* Inactive Users */}
            <div className='bg-white overflow-hidden shadow rounded-lg'>
               <div className='p-3'>
                  <div className='flex items-center'>
                     <div className='flex-shrink-0'>
                        <UserX className='h-5 w-5 text-gray-600' />
                     </div>
                     <div className='ml-3 w-0 flex-1'>
                        <dl>
                           <dt className='text-xs font-medium text-gray-500 truncate'>
                              Inactive Users
                           </dt>
                           <dd className='text-base font-medium text-gray-900'>
                              {(
                                 mapData.metrics.inactive ??
                                 (mapData.metrics.invited ?? 0) +
                                    (mapData.metrics.loggedInNotPaid ?? 0)
                              ).toLocaleString()}
                           </dd>
                        </dl>
                     </div>
                  </div>
               </div>
               <div className='bg-gray-50 px-3 py-2'>
                  <div className='text-xs'>
                     <span className='font-medium text-gray-800'>No activity in last 7 days</span>
                  </div>
               </div>
            </div>
         </div>

         {/* Map Container */}
         <div className='bg-white shadow rounded-lg'>
            <div className='px-3 py-3 sm:p-4'>
               <h3 className='text-base leading-6 font-medium text-gray-900 mb-3'>
                  World Map Distribution
               </h3>
               <div className='h-64 bg-gray-100 rounded-lg overflow-hidden'>
                  <InteractiveMap
                     users={mapData.users.filter((u) => u.location !== null) as any[]}
                     className='h-full w-full'
                  />
               </div>
            </div>
         </div>

         {/* User List with Pagination */}
         <div className='bg-white shadow rounded-lg'>
            <div className='px-3 py-3 sm:p-4'>
               <div className='flex justify-between items-center mb-3'>
                  <h3 className='text-base leading-6 font-medium text-gray-900'>
                     All Users ({sortedUsers.length} total)
                  </h3>
                  <div className='text-xs text-gray-500'>
                     Showing {startIndex + 1}-{Math.min(endIndex, sortedUsers.length)} of{' '}
                     {sortedUsers.length}
                  </div>
               </div>
               <div className='overflow-hidden'>
                  <table className='min-w-full divide-y divide-gray-200'>
                     <thead className='bg-gray-50'>
                        <tr>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              User
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Status
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Location
                           </th>
                           <th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Activity
                           </th>
                        </tr>
                     </thead>
                     <tbody className='bg-white divide-y divide-gray-200'>
                        {currentUsers.map((user) => (
                           <tr key={user.id}>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 <div className='flex items-center'>
                                    {user.profile?.photoURL && (
                                       <img
                                          className='h-6 w-6 rounded-full mr-2'
                                          src={user.profile.photoURL}
                                          alt={`${user.firstName} ${user.lastName}`}
                                          onError={(e) => {
                                             e.currentTarget.style.display = 'none';
                                          }}
                                       />
                                    )}
                                    <div>
                                       <div className='text-xs font-medium text-gray-900'>
                                          {user.firstName} {user.lastName}
                                       </div>
                                       <div className='text-xs text-gray-500'>{user.email}</div>
                                       {user.accountType && (
                                          <div className='text-xs text-gray-400'>
                                             {user.accountType}
                                          </div>
                                       )}
                                    </div>
                                 </div>
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 <span
                                    className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                                       user.status === 'active'
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-gray-100 text-gray-800'
                                    }`}
                                 >
                                    {user.status === 'active' ? 'Active' : 'Inactive'}
                                 </span>
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 {user.location ? (
                                    <div>
                                       <div className='text-xs text-gray-900'>
                                          {user.location.city}, {user.location.country}
                                       </div>
                                       <div className='text-xs text-gray-400'>
                                          {user.location.latitude.toFixed(2)},{' '}
                                          {user.location.longitude.toFixed(2)}
                                       </div>
                                    </div>
                                 ) : (
                                    <div className='text-xs text-gray-500 italic'>
                                       <span className='inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600'>
                                          No location
                                       </span>
                                    </div>
                                 )}
                              </td>
                              <td className='px-3 py-2 whitespace-nowrap'>
                                 <div className='text-xs text-gray-900'>
                                    {new Date(user.registrationDate).toLocaleDateString()}
                                 </div>
                                 {user.lastLogin ? (
                                    <div className='text-xs text-gray-600'>
                                       Login: {new Date(user.lastLogin).toLocaleDateString()}
                                    </div>
                                 ) : (
                                    <div className='text-xs text-gray-400'>Never logged in</div>
                                 )}
                                 {user.lastActivity ? (
                                    <div className='text-xs text-gray-500'>
                                       Activity: {new Date(user.lastActivity).toLocaleDateString()}
                                    </div>
                                 ) : (
                                    <div className='text-xs text-gray-400'>No activity</div>
                                 )}
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>

               {/* Pagination Controls */}
               {totalPages > 1 && (
                  <div className='mt-4 flex items-center justify-between'>
                     <div className='flex-1 flex justify-between sm:hidden'>
                        <button
                           onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                           disabled={currentPage <= 1}
                           className='relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                           Previous
                        </button>
                        <button
                           onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                           disabled={currentPage >= totalPages}
                           className='ml-3 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                           Next
                        </button>
                     </div>
                     <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
                        <div>
                           <p className='text-xs text-gray-700'>
                              Showing <span className='font-medium'>{startIndex + 1}</span> to{' '}
                              <span className='font-medium'>
                                 {Math.min(endIndex, sortedUsers.length)}
                              </span>{' '}
                              of <span className='font-medium'>{sortedUsers.length}</span> results
                           </p>
                        </div>
                        <div>
                           <nav
                              className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                              aria-label='Pagination'
                           >
                              <button
                                 onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                 disabled={currentPage <= 1}
                                 className='relative inline-flex items-center px-1.5 py-1.5 rounded-l-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                 <span className='sr-only'>Previous</span>
                                 <ChevronLeft className='h-4 w-4' />
                              </button>

                              {/* Page Numbers */}
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                 const pageNum = i + 1;
                                 return (
                                    <button
                                       key={pageNum}
                                       onClick={() => setCurrentPage(pageNum)}
                                       className={`relative inline-flex items-center px-2 py-1.5 border text-xs font-medium ${
                                          currentPage === pageNum
                                             ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                             : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                       }`}
                                    >
                                       {pageNum}
                                    </button>
                                 );
                              })}

                              <button
                                 onClick={() =>
                                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                                 }
                                 disabled={currentPage >= totalPages}
                                 className='relative inline-flex items-center px-1.5 py-1.5 rounded-r-md border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                 <span className='sr-only'>Next</span>
                                 <ChevronRight className='h-4 w-4' />
                              </button>
                           </nav>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>
   );
};
